import { apiJson } from "../api/client";
import { ApiError, UnauthorizedError } from "../api/errors";
import type {
  TeamAppCurrentUser,
  TeamAppFeatureFlags,
  TeamAppProps,
} from "./teamAppZoid";
import {
  DEFAULT_DEV_HOST_IDENTITY,
  isDevHostEnabled,
  loadDevHostIdentity,
  loadDevHostOverrides,
} from "./devHostConfig";

// Dev-only host shim ("dev sim").
//
// When the app runs embedded, zoid populates `window.xprops` after the
// iframe handshake and `getHost()` returns the real bridge. Opening the
// Vite dev server directly (http://localhost:5173), or a per-PR sandbox URL
// (https://pr-<n>.team-management.sandbox.ahaslide.com), is NOT embedded, so
// `xprops` stays undefined, `getHost()` returns null, and the owner/admin
// toolbar (LicenseUsageMeter + ActionButtons, including the Export button)
// never mounts — there's nothing to test against.
//
// This seeds a mock `xprops` so the toolbar renders standalone. It runs in
// two situations only (see isDevHostEnabled):
//   1. `import.meta.env.DEV` — the Vite dev server (never a build).
//   2. `VITE_ENABLE_DEV_HOST === "true"` — set ONLY in `.env.sandbox`, so the
//      per-PR sandbox builds render the toolbar for visual review. staging /
//      canary / production `.env.*` never set the flag → the shim stays inert.
// It is otherwise impossible to activate against a real host:
//   - `window.xprops` already set — a real zoid embed wins; we no-op.
//   - The bridge fns are console stubs, so nothing leaves the page.
//
// The identity context (currentUser + currentLanguage) is resolved from
// three layers, low → high precedence:
//   1. DEFAULT_DEV_HOST_IDENTITY   — a hardcoded owner (full UI unlocked).
//   2. GET /api/profile            — the REAL authenticated user, when the
//      origin already has an `authToken` cookie (dev01 / sandbox). Lets the
//      standalone build render against your actual account/id without
//      hand-typing them, so useAccount(accountId) fetches real data.
//   3. DevHostPanel overrides      — explicit dev edits in localStorage, so
//      you can still flip accountRole owner → viewer etc. at runtime. The
//      panel's "Reset" clears these, falling back to the real profile.
// The bridge callbacks below are fixed — they're functions, not data.
//
// AUTH CAVEAT: this shim supplies host *identity* only, not a host token.
// The app still calls the backend with credentials; on a fresh sandbox
// origin there's no `authToken` cookie yet, so the /profile fetch 401s
// (identity falls back to the mock) and member/invitation rows are empty
// until you're authenticated against the dev01 backend — same as opening
// the dev server before logging in. The toolbar, tabs, and layout render
// regardless, which is what the sandbox is for.

/** Convert the resolved flat `{ name: enabled }` map (defaults + the panel's
 *  overrides) into the wire `TeamAppFeatureFlags` shape (`{ name: { enabled } }`)
 *  the embed reads via isHostFeatureEnabled(). */
function toFeatureFlags(flags: Record<string, boolean>): TeamAppFeatureFlags {
  const out: TeamAppFeatureFlags = {};
  for (const [name, enabled] of Object.entries(flags)) {
    out[name] = { enabled };
  }
  return out;
}

function buildMockHost(): TeamAppProps {
  const { currentUser, currentLanguage, featureFlags } = loadDevHostIdentity();
  return {
    url: window.location.href,
    // Configurable identity (defaults to an owner, which unlocks the full
    // UI). Edited live via DevHostPanel; see devHostConfig.ts.
    currentUser,
    currentLanguage,
    // Toast / track bridges → console so the flows don't throw standalone.
    showToastSuccess: (text) => console.info("[dev-host] toast.success:", text),
    showToastError: (text) => console.info("[dev-host] toast.error:", text),
    showToastInfo: (text) => console.info("[dev-host] toast.info:", text),
    showConfirmModal: async (payload) => {
      console.info("[dev-host] confirm:", payload);
      return window.confirm(`${payload.title}\n\n${payload.content}`);
    },
    trackGA4AndMixpanel: (eventName, eventPayload) =>
      console.info("[dev-host] track:", eventName, eventPayload),
    onRouteChange: (payload) => console.info("[dev-host] routeChange:", payload),
    onHeightChange: () => {},
    executeRecaptcha: async () => null,
    openRequestAdditionalLicenseModal: () =>
      console.info("[dev-host] openRequestAdditionalLicenseModal"),
    // Seed feature flags from the dev-host config: the defaults in
    // DEFAULT_DEV_HOST_IDENTITY (which turn the email-verification badge on),
    // with any DevHostPanel override layered over them. Real embeds never see
    // this — window.xprops from zoid wins and this mock is skipped entirely
    // (see installDevHostShim below).
    featureFlags: toFeatureFlags(featureFlags),
    onSelectionChange: (state) => console.info("[dev-host] selection:", state),
    onBulkActionComplete: () => {},
  };
}

// Shape of the backend `GET /api/profile` response (its AuthenticatedUser).
// Mirrored locally — that type lives in the backend workspace and isn't
// published, so we declare only the fields the shim maps onto currentUser.
interface DevHostProfile {
  id: string;
  email: string;
  accountId: number | null;
  accountRole: string | null;
  role: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

/** Fetch the authenticated profile, or null when the origin has no valid
 *  `authToken` cookie yet (401) or the backend is unreachable. Never throws
 *  — a missing profile just leaves the mock identity in place, but we log
 *  *why* so "identity didn't populate" is never a silent mystery. */
async function fetchDevHostProfile(): Promise<DevHostProfile | null> {
  try {
    return await apiJson<DevHostProfile>("/profile");
  } catch (err) {
    // 401 is the common, expected case on a fresh sandbox origin: standalone
    // there's no zoid host to supply a Bearer token, and no `authToken`
    // cookie has been minted yet, so the request is unauthenticated. This is
    // NOT a merge bug — it means "not signed in to the dev01 backend in this
    // browser". Sign in there (so the credentialed same-site request carries
    // the session cookie) to populate real identity.
    const reason =
      err instanceof UnauthorizedError
        ? "401 unauthorized — no authToken cookie / host Bearer token on this origin"
        : err instanceof ApiError
          ? `HTTP ${err.status}`
          : String(err);
    console.info(
      `[dev-host] GET /api/profile failed (${reason}); keeping mock identity. ` +
        "Sign in to the dev01 backend in this browser to populate real identity.",
    );
    return null;
  }
}

function dropUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as Partial<T>;
}

/** Map the /profile response onto the currentUser fields it can supply.
 *  Fields absent from /profile (presenterLanguage, planType, whiteLabel*)
 *  are omitted so lower/higher layers fill them; `undefined` values are
 *  dropped so a spread-merge never clobbers a real default. */
function profileToCurrentUser(p: DevHostProfile): Partial<TeamAppCurrentUser> {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return dropUndefined<TeamAppCurrentUser>({
    id: p.id,
    name: name || undefined,
    email: p.email ?? undefined,
    accountId: p.accountId != null ? String(p.accountId) : undefined,
    accountRole: p.accountRole ?? undefined,
    role: p.role ?? undefined,
    avatarUrl: p.avatarUrl ?? undefined,
  });
}

/**
 * Install the mock host on `window.xprops` when running standalone. No-ops
 * when the shim is disabled (see isDevHostEnabled) and when a real zoid host
 * is present. Call once on bootstrap and `await` it before `createRoot` so
 * the enriched identity is in place before the first render.
 */
export async function installDevHostShim(): Promise<void> {
  if (!isDevHostEnabled()) return;
  const w = window as { xprops?: TeamAppProps };
  if (w.xprops) return; // real zoid embed — leave it alone

  // Seed synchronously first: this makes `window.xprops` (and the mock's
  // undefined `token`) available immediately, so the /profile fetch below
  // resolves its session token at once instead of polling 1.5s for a zoid
  // bridge that never appears standalone.
  w.xprops = buildMockHost();

  // Enrich with the real authenticated identity when available. Precedence
  // low → high: defaults < /profile response < DevHostPanel overrides — so
  // panel edits still win and "Reset" falls back to the real profile.
  const profile = await fetchDevHostProfile();
  if (profile) {
    const overrides = loadDevHostOverrides();
    w.xprops.currentUser = {
      ...DEFAULT_DEV_HOST_IDENTITY.currentUser,
      ...profileToCurrentUser(profile),
      ...(overrides.currentUser ?? {}),
    };
    console.info(
      "[dev-host] Identity populated from GET /api/profile:",
      w.xprops.currentUser,
    );
  }

  console.warn(
    "[dev-host] No zoid host detected — installed mock xprops. " +
      "The owner toolbar (incl. Export) is now visible. " +
      "Enabled by import.meta.env.DEV or VITE_ENABLE_DEV_HOST=true (sandbox only).",
  );
}
